// @flow

/**
 * history@4-compatible history that keeps the module route in ONE query
 * parameter of the host page (`?shop=/product/…`) instead of the URL fragment.
 *
 * Why: query URLs are distinct, indexable addresses for search engines and
 * need no server rewrite on the dealer's site, unlike path-based deeplinks.
 * Every other parameter of the host page (WordPress `?page_id=`, utm_*) is
 * preserved untouched; the host page's own `#anchor` is ignored.
 *
 * Deliberately free of app imports (store, config) so it can move into the
 * history factory of the Vite/TS branch as a fourth HistoryMode.
 */

export const DEFAULT_PARAM_NAME = "shop";

const HEX_PAIR = /^[0-9a-fA-F]{2}$/;
const KEEP_UNENCODED = /[A-Za-z0-9\-_.!~*()/?:@=,;$]/;

/**
 * Encodes a route for use as query value: separators that would break the
 * query string (`&`, `#`, `+`, space, quotes) and non-ASCII characters are
 * percent-encoded, `/` and already encoded `%XX` sequences stay as they are.
 * `'` is encoded too: browsers send it as %27 in a query anyway, and mail or
 * chat link detection ends a link at a bare `'` (19''-Felgen …).
 * Mirrors ShopUrlBuilder.encodeRouteForQuery in product-catalog-rest-api,
 * except for `'`, which the backend still keeps.
 */
export const encodeRouteForQuery = (route: string): string => {
    let out = "";
    let index = 0;
    for (const ch of route) {
        if (ch === "%") {
            out += HEX_PAIR.test(route.substr(index + 1, 2)) ? "%" : "%25";
        } else if (KEEP_UNENCODED.test(ch)) {
            out += ch;
        } else {
            out += ch === "'" ? "%27" : encodeURIComponent(ch);
        }
        index += ch.length;
    }
    return out;
};

// mirrors history@4's createLocation: decodeURI keeps reserved characters
// (%26, %23, %2F, …) encoded, so a name segment round-trips unchanged
export const decodeRoute = (raw: string): string => {
    try {
        return decodeURI(raw);
    } catch (e) {
        return raw;
    }
};

const paramPattern = (paramName: string): RegExp => new RegExp(`[?&]${paramName}=([^&#]*)`);

export const readRouteFromSearch = (search: string, paramName: string = DEFAULT_PARAM_NAME): ?string => {
    const match = paramPattern(paramName).exec(search || "");
    if (!match) {
        return null;
    }
    const route = decodeRoute(match[1]);
    if (!route) {
        return "/";
    }
    return route.charAt(0) === "/" ? route : `/${route}`;
};

/**
 * Returns the host search string with the route parameter set (in place if
 * present, appended otherwise). The home route `/` removes the parameter so
 * the host page keeps its clean address.
 */
export const writeRouteToSearch = (search: string, paramName: string, route: string): string => {
    const entries = (search || "").replace(/^\?/, "").split("&").filter(entry => entry !== "");
    const prefix = `${paramName}=`;
    const index = entries.findIndex(entry => entry === paramName || entry.startsWith(prefix));
    const encoded = (!route || route === "/") ? null : prefix + encodeRouteForQuery(route);

    if (index === -1) {
        if (encoded) {
            entries.push(encoded);
        }
    } else if (encoded) {
        entries[index] = encoded;
    } else {
        entries.splice(index, 1);
    }
    return entries.length ? `?${entries.join("&")}` : "";
};

export const parsePath = (path: string): {pathname: string, search: string, hash: string} => {
    let pathname = path || "/";
    let search = "";
    let hash = "";

    const hashIndex = pathname.indexOf("#");
    if (hashIndex !== -1) {
        hash = pathname.substr(hashIndex);
        pathname = pathname.substr(0, hashIndex);
    }
    const searchIndex = pathname.indexOf("?");
    if (searchIndex !== -1) {
        search = pathname.substr(searchIndex);
        pathname = pathname.substr(0, searchIndex);
    }
    return {
        pathname,
        search: search === "?" ? "" : search,
        hash: hash === "#" ? "" : hash,
    };
};

export const createPath = (location: {pathname?: string, search?: string, hash?: string}): string => {
    const {pathname, search, hash} = location;
    let path = pathname || "/";
    if (search && search !== "?") {
        path += search.charAt(0) === "?" ? search : `?${search}`;
    }
    if (hash && hash !== "#") {
        path += hash.charAt(0) === "#" ? hash : `#${hash}`;
    }
    return path;
};

const resolvePathname = (to: string, from: string): string => {
    const base = from.split("/");
    base.pop();
    const resolved = [];
    base.concat(to.split("/")).forEach(segment => {
        if (segment === "..") {
            resolved.pop();
        } else if (segment !== "." && segment !== "") {
            resolved.push(segment);
        }
    });
    return `/${resolved.join("/")}${to.endsWith("/") && resolved.length ? "/" : ""}`;
};

const createLocation = (path: any, state: any, key: ?string, currentLocation: ?Object): Object => {
    let location;
    if (typeof path === "string") {
        location = {...parsePath(path), state};
    } else {
        location = {...path};
        location.pathname = location.pathname || "";
        location.search = location.search ? (location.search.charAt(0) === "?" ? location.search : `?${location.search}`) : "";
        location.hash = location.hash ? (location.hash.charAt(0) === "#" ? location.hash : `#${location.hash}`) : "";
        if (state !== undefined && location.state === undefined) {
            location.state = state;
        }
    }

    location.pathname = decodeRoute(location.pathname);

    if (key) {
        location.key = key;
    }
    if (currentLocation) {
        if (!location.pathname) {
            location.pathname = currentLocation.pathname;
        } else if (location.pathname.charAt(0) !== "/") {
            location.pathname = resolvePathname(location.pathname, currentLocation.pathname);
        }
    } else if (!location.pathname) {
        location.pathname = "/";
    }
    return location;
};

const createKey = (): string => Math.random().toString(36).substr(2, 6);

// WebKit fires an extraneous popstate on load (no state); mirrors history@4
const isExtraneousPopstateEvent = (event: PopStateEvent): boolean =>
    event.state === undefined && navigator.userAgent.indexOf("CriOS") === -1;

export const createQueryHistory = (options: {paramName?: string} = {}): any => {
    const paramName = options.paramName || DEFAULT_PARAM_NAME;
    const globalHistory = window.history;
    const listeners = [];
    let prompt = null;

    // Links from before query routing (feed, mails, dealer finder, bookmarks) carry the route in the
    // fragment: `#/product/…`. Without the parameter that route wins at boot and the address is rewritten
    // once (replaceState, so Back leads to the link's source). The host page's own anchor (`#kontakt`)
    // and a URL that already has the parameter are left alone.
    const adoptLegacyHashRoute = () => {
        const {search, hash, pathname} = window.location;
        if (readRouteFromSearch(search, paramName) != null || !hash.startsWith("#/")) {
            return;
        }
        globalHistory.replaceState(globalHistory.state, "", pathname + writeRouteToSearch(search, paramName, decodeRoute(hash.slice(1))));
    };

    const getDOMLocation = (historyState: any) => {
        const {key, state} = historyState || {};
        return createLocation(readRouteFromSearch(window.location.search, paramName) || "/", state, key);
    };

    const createHref = (location: any): string => {
        const target = typeof location === "string" ? parsePath(location) : location;
        return window.location.pathname + writeRouteToSearch(window.location.search, paramName, createPath(target));
    };

    adoptLegacyHashRoute();

    const history: any = {
        length: globalHistory.length,
        action: "POP",
        location: getDOMLocation(globalHistory.state),
        createHref,
    };

    const setState = (action: string, location: Object) => {
        history.action = action;
        history.location = location;
        history.length = globalHistory.length;
        listeners.slice().forEach(listener => listener(history.location, history.action));
    };

    const confirmTransition = (location: Object, action: string): boolean => {
        if (prompt == null) {
            return true;
        }
        const result = typeof prompt === "function" ? prompt(location, action) : prompt;
        if (typeof result === "string") {
            return window.confirm(result);
        }
        return result !== false;
    };

    const handlePopState = (event: PopStateEvent) => {
        if (isExtraneousPopstateEvent(event)) {
            return;
        }
        const location = getDOMLocation(event.state);
        if (confirmTransition(location, "POP")) {
            setState("POP", location);
        }
    };

    history.push = (path: any, state: any) => {
        const location = createLocation(path, state, createKey(), history.location);
        if (!confirmTransition(location, "PUSH")) {
            return;
        }
        globalHistory.pushState({key: location.key, state: location.state}, "", createHref(location));
        setState("PUSH", location);
    };

    history.replace = (path: any, state: any) => {
        const location = createLocation(path, state, createKey(), history.location);
        if (!confirmTransition(location, "REPLACE")) {
            return;
        }
        globalHistory.replaceState({key: location.key, state: location.state}, "", createHref(location));
        setState("REPLACE", location);
    };

    history.go = (n: number) => globalHistory.go(n);
    history.goBack = () => history.go(-1);
    history.goForward = () => history.go(1);

    history.listen = (listener: Function) => {
        listeners.push(listener);
        if (listeners.length === 1) {
            window.addEventListener("popstate", handlePopState);
        }
        return () => {
            const index = listeners.indexOf(listener);
            if (index !== -1) {
                listeners.splice(index, 1);
            }
            if (listeners.length === 0) {
                window.removeEventListener("popstate", handlePopState);
            }
        };
    };

    history.block = (nextPrompt: any = false) => {
        prompt = nextPrompt;
        return () => {
            if (prompt === nextPrompt) {
                prompt = null;
            }
        };
    };

    return history;
};
